import { Alert, AlertColor, Box, Snackbar } from "@mui/material";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button } from "@mui/material";

export type AlertAction = {
  label: string;
  onClick: () => void;
};

type AlertMessage = {
  body: string;
  type: AlertColor;
  action?: AlertAction;
};

type AlertContextValue = {
  alertError: (body: string, action?: AlertAction) => void;
  alertSuccess: (s: string) => void;
  alertInfo: (s: string) => void;
  alertWarning: (s: string) => void;
};

const AlertContext = createContext<AlertContextValue>({
  alertError: (_) => {},
  alertSuccess: (_) => {},
  alertInfo: (_) => {},
  alertWarning: (_) => {},
});

export const AlertProvider = ({ children }: { children: React.ReactNode }) => {
  const [alerts, setAlerts] = useState<AlertMessage[]>([]);
  const timeouts = useRef<NodeJS.Timeout[]>([]);
  const removeAlert = useCallback(() => {
    timeouts.current.forEach((timeout) => {
      clearTimeout(timeout);
    });
    const timeout = setTimeout(() => {
      setAlerts(([_toBeRemoved, ...alerts]) => alerts); // eslint-disable-line @typescript-eslint/no-shadow
    }, 10000);
    timeouts.current = [timeout];
  }, [setAlerts]);

  const clearDismissals = useCallback(() => {
    timeouts.current.forEach((timeout) => {
      clearTimeout(timeout);
    });
    timeouts.current = [];
  }, []);

  const alertError = useCallback(
    (body: string, action?: AlertAction) => {
      if (action) clearDismissals();
      setAlerts(() => [{ body, type: "error", action }]);
      if (!action) removeAlert();
    },
    [setAlerts], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const alertSuccess = useCallback(
    (body: string) => {
      setAlerts(() => [{ body, type: "success" }]);
      removeAlert();
    },
    [setAlerts, alerts], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const alertInfo = useCallback(
    (body: string) => {
      setAlerts(() => [{ body, type: "info" }]);
      removeAlert();
    },
    [setAlerts], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const alertWarning = useCallback(
    (body: string) => {
      setAlerts((_alerts) => [{ body, type: "warning" }]);
      removeAlert();
    },
    [setAlerts], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    return () => {
      timeouts.current.forEach((timeout) => {
        clearTimeout(timeout);
      });
    };
  }, []);

  useEffect(() => {
    const handleAlertError = (event: Event) => {
      if ("detail" in event && typeof (event as CustomEvent<string>).detail === "string") {
        alertError((event as CustomEvent<string>).detail);
      }
    };

    window.addEventListener("alertError", handleAlertError);

    return () => {
      window.removeEventListener("alertError", handleAlertError);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleAlertSuccess = (event: Event) => {
      if ("detail" in event && typeof (event as CustomEvent<string>).detail === "string") {
        alertSuccess((event as CustomEvent<string>).detail);
      }
    };

    window.addEventListener("alertSuccess", handleAlertSuccess);

    return () => {
      window.removeEventListener("alertSuccess", handleAlertSuccess);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleAlertInfo = (event: Event) => {
      if ("detail" in event && typeof (event as CustomEvent<string>).detail === "string") {
        alertInfo((event as CustomEvent<string>).detail);
      }
    };

    window.addEventListener("alertInfo", handleAlertInfo);

    return () => {
      window.removeEventListener("alertInfo", handleAlertInfo);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleAlertWarning = (event: Event) => {
      if ("detail" in event && typeof (event as CustomEvent<string>).detail === "string") {
        alertWarning((event as CustomEvent<string>).detail);
      }
    };

    window.addEventListener("alertWarning", handleAlertWarning);

    return () => {
      window.removeEventListener("alertWarning", handleAlertWarning);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    alertError,
    alertSuccess,
    alertInfo,
    alertWarning,
  };

  return (
    <AlertContext.Provider value={value}>
      {children}
      <Snackbar
        open={true}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box data-testid="alerts-container">
          {alerts.map((alert, index) => (
            <Alert
              key={index}
              severity={alert.type}
              data-testid={`alerts-message-${alert.type}`}
              sx={{ whiteSpace: "pre-line" }}
              onClose={alert.action ? () => setAlerts([]) : undefined}
            >
              {alert.body}
              {alert.action ? (
                <Box>
                  <Button
                    color="inherit"
                    size="small"
                    data-testid="alerts-message-action"
                    onClick={() => {
                      alert.action?.onClick();
                      setAlerts([]);
                    }}
                    sx={{ mt: 1 }}
                  >
                    {alert.action.label}
                  </Button>
                </Box>
              ) : null}
            </Alert>
          ))}
        </Box>
      </Snackbar>
    </AlertContext.Provider>
  );
};

export const useAlert = () => {
  const { alertError, alertInfo, alertSuccess, alertWarning } = useContext(AlertContext);

  return {
    alertError,
    alertInfo,
    alertSuccess,
    alertWarning,
  };
};

export const useGlobalAlert = {
  alertError: (body: string) => {
    window.dispatchEvent(new CustomEvent("alertError", { detail: body }));
  },
  alertInfo: (body: string) => {
    window.dispatchEvent(new CustomEvent("alertInfo", { detail: body }));
  },
  alertSuccess: (body: string) => {
    window.dispatchEvent(new CustomEvent("alertSuccess", { detail: body }));
  },
  alertWarning: (body: string) => {
    window.dispatchEvent(new CustomEvent("alertWarning", { detail: body }));
  },
};
